import { FirebaseApp } from 'firebase/app';
import { collection, doc, query, where, setDoc, getDoc, getFirestore, Firestore, updateDoc, deleteDoc, getDocs, WhereFilterOp, addDoc, or } from 'firebase/firestore';
import { Model } from '../../model/Model';
import { IModelService } from '../IModelService';
import { IModelDataConverter } from '../IModelDataConverter';
import { ModelDataConverter } from './ModelDataConverter';

export abstract class ModelService<T extends Model> implements IModelService<T> {
  protected database: Firestore;
  protected path: string;
  protected converter: IModelDataConverter<T>;

  constructor(app: FirebaseApp, path: string) {
    this.path = path;
    this.database = getFirestore(app);
    this.converter = new ModelDataConverter<T>();
  }

  async get(id: string): Promise<T | null> {
    const ref = doc(this.database, this.path, id).withConverter(this.converter);
    const snapshot = await getDoc(ref);
    if(snapshot.exists()) {
      return snapshot.data();
    }
    return null;
  }

  async create(model: T): Promise<T> {
    const ref = collection(this.database, this.path).withConverter(this.converter);
    await addDoc(ref, model);
    model.id = ref.id;
    return model;
  }

  async update(id: string, fields: any): Promise<void> {
    const ref = doc(this.database, this.path, id).withConverter(this.converter);
    await updateDoc(ref, fields);
  }

  async createOrUpdate(model: T): Promise<T> {
    const ref = doc(this.database, this.path, model.id || '');
    await setDoc(ref, model);
    model.id = ref.id;
    return model;
  }

  async delete(model: T): Promise<void> {
    if(model.id) {
      const ref = doc(this.database, this.path, model.id || '');
      await deleteDoc(ref);
    }
  }

  async all(): Promise<T[]> {
    const ref = collection(this.database, this.path).withConverter(this.converter);
    const docs = await getDocs(ref);
    return docs.docs.map(d => d.data());
  }

  async andWhere(conditions: { field: string, operator: string, value: any }[]): Promise<T[]> {
    const ref = collection(this.database, this.path).withConverter(this.converter);
    const queries = conditions.map(cond => {
      let o = cond.operator as WhereFilterOp;
      o = o || '==';
      return where(cond.field, o, cond.value);
    });
    const q = query(ref, ...queries);
    const docs = await getDocs(q);
    return docs.docs.map(d => d.data());
  }
  
  async orWhere(conditions: { field: string, operator: string, value: any }[]): Promise<T[]> {
    const ref = collection(this.database, this.path).withConverter(this.converter);
    const queries = conditions.map(cond => {
      let o = cond.operator as WhereFilterOp;
      o = o || '==';
      return where(cond.field, o, cond.value);
    });
    const q = query(ref, or(...queries));
    const docs = await getDocs(q);
    return docs.docs.map(d => d.data());
  }
} 